import React, { useCallback } from "react";
import { useIntl } from "react-intl";
import * as _ from "lodash-es";
import IPrice_Group from "@models/IPrice_Group";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwToggle from "@components/shared/OwToggle/OwToggle";

/**
 * Catalog item pricing keyed by price group value. "N/A" marks the item as not available
 * (hidden from order entry) for that price group; the API filters on that sentinel.
 */
export type ICatalogItemPricing = { [priceGroupValue: string]: number | "N/A" };

type Props = {
    priceGroups: IPrice_Group[];
    pricing: ICatalogItemPricing;
    /**
     * Receives the full pricing object with the toggled price group updated; the sidebar stores it
     * on the item and sends it through the existing create/update catalog item endpoints.
     */
    handleUpdatePricing: (pricing: ICatalogItemPricing) => void;
};

/**
 * A price group is available unless its pricing entry is the "N/A" sentinel. A missing entry counts
 * as available, matching the sidebar which defaults missing price groups to 0.
 */
export const isAvailableInPriceGroup = (pricing: ICatalogItemPricing, priceGroupValue: string): boolean => {
    return _.get(pricing, priceGroupValue) !== "N/A";
};

/**
 * Returns a copy of `pricing` with the price group switched on or off. A product group has no price of
 * its own (its items carry the prices), so switching it on stores 0, the sidebar's default price,
 * unless a numeric price is already present.
 */
export const setPriceGroupAvailability = (
    pricing: ICatalogItemPricing,
    priceGroupValue: string,
    available: boolean
): ICatalogItemPricing => {
    const update: ICatalogItemPricing = { ...pricing };
    const current = pricing[priceGroupValue];

    if (available) {
        update[priceGroupValue] = typeof current === "number" ? current : 0;
    } else {
        update[priceGroupValue] = "N/A";
    }

    return update;
};

/**
 * Per-price-group availability toggles for a product group, rendered where the standard price details
 * section sits for every other catalog item so pricing is always found in the same place.
 *
 * Product groups do not render the price inputs (they carry no price), so without this section a group
 * that had been marked "N/A" in every price group disappeared from order entry with nothing in the UI
 * to switch it back on. The toggles always render for every price group, whatever the group's current
 * availability, so a supplier can re-enable it themselves.
 */
const ProductGroupPriceGroupAvailabilityInner = (props: Props) => {
    const { priceGroups, pricing, handleUpdatePricing } = props;
    const intl = useIntl();

    const handleToggle = useCallback(
        (e: { target: { value: boolean | number; name: string } }) => {
            // OwToggle sends e.target.value as true (switched on) or false (switched off).
            const available = e.target.value === true || e.target.value === 1;

            handleUpdatePricing(setPriceGroupAvailability(pricing, e.target.name, available));
        },
        [pricing, handleUpdatePricing]
    );

    if (!priceGroups || priceGroups.length === 0) {
        return null;
    }

    const availableLabel = intl.formatMessage({ id: "available", defaultMessage: "_available_" });
    const noneAvailable = priceGroups.every((priceGroup) => !isAvailableInPriceGroup(pricing, priceGroup.value));

    return (
        <div className={"ProductGroupPriceGroupAvailability"}>
            <div className={"bd-callout"}>
                <OwLabel title={"price_details"} leftIconClass={"fad fa-dollar-sign"} />
            </div>
            <div className={"bd-callout"}>
                <OwLabel title={"price_groups"} leftIconClass={"fad fa-tags"} />
            </div>
            <div className={"d-flex mb-3"}>
                <div className={"me-2"}>
                    <i className={"fad fa-info-circle"} />
                </div>
                <div className={"text-small text-muted"}>
                    <OwLabel title={"help_text_product_group_price_group_availability"} noBottomMargin={true} />
                </div>
            </div>
            {noneAvailable ? (
                <div className={"d-flex mb-3 text-warning product-group-none-available"} role={"alert"}>
                    <div className={"me-2"}>
                        <i className={"fad fa-exclamation-triangle"} />
                    </div>
                    <div className={"text-small"}>
                        <OwLabel title={"product_group_not_available_in_any_price_group"} noBottomMargin={true} />
                    </div>
                </div>
            ) : null}
            {priceGroups.map((priceGroup) => (
                <div key={priceGroup.value} className={"mb-2 product-group-price-group-row"}>
                    <OwToggle
                        name={priceGroup.value}
                        label={priceGroup.value}
                        disableTranslation={true}
                        active={isAvailableInPriceGroup(pricing, priceGroup.value)}
                        fn={handleToggle}
                        labelOnRight={true}
                        ariaLabel={`${priceGroup.value} ${availableLabel}`}
                    />
                </div>
            ))}
        </div>
    );
};

export const ProductGroupPriceGroupAvailability = React.memo(ProductGroupPriceGroupAvailabilityInner);

export default ProductGroupPriceGroupAvailability;
